import axios, { AxiosHeaders, AxiosInstance } from 'axios';
import SSOService from '../../controllers/login-services/SSOService';
import Endpoints from '../Endpoints';

export default class ServerInstanceSingleton {
  private static instance: ServerInstanceSingleton | null = null;
  private axiosInstance: AxiosInstance;

  private constructor() {
    const urls = Endpoints.getInstance();
    this.axiosInstance = axios.create({ baseURL: urls.ENV_API_PATH });

    this.axiosInstance.interceptors.request.use(async (config) => {
      const ssoService = SSOService.getInstance();

      if (!ssoService.getAccessToken() || ssoService.isAccessTokenExpired()) {
        try {
          await this.updateTokenFromRefresh();
        } catch {
          ssoService.doLogin();
          return Promise.reject(new Error("Refresh token fallito, redirect login"));
        }
      }

      const token = ssoService.getAccessToken();
      if (token) {
        config.headers = {
          ...(config.headers ?? {}),
          Authorization: `Bearer ${token}`,
        };
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      response => response.data,
      async error => {
        return Promise.reject(error);
      }
    );
  }

  public static getInstance(): ServerInstanceSingleton {
    if (!ServerInstanceSingleton.instance) {
      ServerInstanceSingleton.instance = new ServerInstanceSingleton();
    }
    return ServerInstanceSingleton.instance;
  }

  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }

  public async updateTokenFromRefresh(): Promise<string> {
    const refreshToken = SSOService.getInstance().getRefreshToken();
    if (!refreshToken) throw new Error("No refresh token available");
    const endpointService = Endpoints.getInstance();
    const env = await endpointService.getEnvironment();
    if (!env || !env.BACKEND_URL) throw new Error("Auth config missing");
    const response = await fetch(
      env.BACKEND_URL + "api/v1/auth/refresh-token",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refreshToken }),
      }
    );

    if (!response.ok) throw new Error("Refresh token failed");

    const data = await response.json();
    const ssoService = SSOService.getInstance();
    if(data.access_token)
      ssoService.setAccessToken(data.access_token);
    if(data.refresh_token)
      ssoService.setRefreshToken(data.refresh_token);

    return data.access_token;
  }
}

